import {
  AlertTriangle, Ban, Check, Phone, PhoneMissed, PhoneOff, PhoneOutgoing, RotateCcw, X,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/feedback";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { acdCall, forcedAnswer } from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { leadName } from "@/lib/campaign";
import { dialSteps, dialWaitText, type StripPart } from "@/lib/dialer";
import {
  chosenCampaign, dialPath, failureText, inProgress, ownCallToAnswer, placedFor, playsRingback,
  progressSteps, secondsSince, stageText, wasConnected, type DialPath, type FailureKind,
} from "@/lib/manual-call";
import { isPstnNumber } from "@/lib/phone-number";
import {
  useAcdMe, useCancelManualCall, useManualCall, useManualDialMemory, useSetManualDialMemory,
  useStartManualCall,
} from "@/lib/queries";
import type { AgentLine, ManualCall } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDuration, formatPhoneNumber } from "@/lib/utils";
import { readAudioPrefs } from "@/softphone/audio-devices";
import { DialStrip } from "@/softphone/dial-strip";
import { useSoftphone } from "@/softphone/softphone-context";
import { Tones } from "@/softphone/tones";
import { seesCampaigns } from "@/lib/types";

/**
 * A number the rep dials by hand, placed through their campaign (ADR-047 in the platform repo).
 *
 * The rep types an outside number and presses Call. Instead of the phone dialling it, core-api
 * hands it to the dialer, which originates the lead leg the way it does a preview dial; when the
 * lead answers, the ACD rings this phone and joins the two in the call's room — and from there
 * the call is a campaign call, with Send to a closer, the roster and the rest. The rep's own leg
 * is not up while the lead rings, so this file is what they see and hear in the meantime: a card
 * that follows the call by its id (`useManualCall`) through placing, ringing and answered, with
 * Cancel; the console's own ringback while the far end rings; the phone answering the ACD's ring
 * for the call it asked for; and, when nobody picks up, a result the rep can act on.
 *
 * `useOutboundDial` is where a destination chooses its path. Every surface that dials an
 * outside number — the Dial box, a number in a call table, a missed call's Call back — goes
 * through it, so an extension still rings a colleague directly from all of them and an outside
 * number goes through the campaign from all of them.
 */

// --- placing --------------------------------------------------------------------------------------

/**
 * How the phone places a destination, and the act of placing it.
 *
 * `path` answers before the rep presses Call, so the Dial box can say where the call will go.
 * `place` does it: through the campaign when `path` says so — the campaign the caller names,
 * else the one the rep chose last time, else the only one they can call through, else core-api
 * decides (the campaign whose list holds the lead) or refuses — and from the phone otherwise.
 * A refusal from core-api is an `ApiError` with its sentence; a refusal from the phone reports
 * itself in the phone's banner, as `phone.dial` always has.
 */
export function useOutboundDial(): {
  path: (destination: string) => DialPath;
  /** `leadId`: the lead the call is for, when it is placed from their record (ACL-382). */
  place: (destination: string, campaignId?: string | null, leadId?: string | null) => Promise<void>;
  placing: boolean;
  /** The campaign the rep chose last, which the picker offers first. */
  remembered: string | null;
  remember: (campaignId: string | null) => void;
} {
  const { user } = useAuth();
  const sees = seesCampaigns(user?.role);
  const phone = useSoftphone();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const memory = useManualDialMemory();
  const setMemory = useSetManualDialMemory();
  const start = useStartManualCall();
  const path = React.useCallback(
    (destination: string): DialPath => dialPath(destination, me, isPstnNumber),
    [me],
  );
  const { dial } = phone;
  const { mutateAsync } = start;
  const remembered = memory.campaignId;
  const place = React.useCallback(
    async (destination: string, campaignId?: string | null, leadId?: string | null) => {
      const via = path(destination);
      if (via.via === "phone") {
        await dial(destination);
        return;
      }
      // A rep never chooses (ACL-652), and a choice remembered from before is not theirs to keep:
      // with none, core-api picks the campaign. A call back still names the call's own.
      const chosen =
        campaignId ?? (sees ? chosenCampaign(via.campaigns, remembered)?.campaign_id : null) ?? null;
      await mutateAsync({ phone: destination, campaignId: chosen, leadId });
    },
    [path, dial, mutateAsync, remembered, sees],
  );
  const remember = React.useCallback(
    (campaignId: string | null) => setMemory({ campaignId }),
    [setMemory],
  );
  return { path, place, placing: start.isPending, remembered, remember };
}

/**
 * Which campaign an outside number goes through, when the rep can use more than one. The
 * same control under the Dial box and in the confirmation off a number in a table, so the rep
 * meets one picker, and choosing in either place is remembered for both.
 */
export function CampaignPicker({
  campaigns, value, onChange, disabled, id,
}: {
  campaigns: readonly AgentLine[];
  value: string | null;
  onChange: (campaignId: string) => void;
  disabled?: boolean;
  id?: string;
}) {
  const fieldId = React.useId();
  const labelledBy = id ?? fieldId;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={labelledBy} className="shrink-0 text-xs text-text-muted">
        Through
      </label>
      <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={labelledBy} className="h-8 min-w-0 flex-1 text-xs">
          <SelectValue placeholder="Choose a campaign" />
        </SelectTrigger>
        <SelectContent>
          {campaigns.map((c) => (
            <SelectItem key={c.campaign_id} value={c.campaign_id as string}>
              {c.campaign_name ?? c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// --- following ------------------------------------------------------------------------------------

/**
 * The call the phone is following, and whether the dock shows it: while it is on its way, and
 * after a failure until the rep puts the result away. Once connected the phone's own call view
 * has it, and once a connected call ends the line's outcome window follows — the card has
 * nothing to add to either.
 */
export function useManualCallWork(): { call: ManualCall | null; shown: boolean } {
  const memory = useManualDialMemory();
  const { data } = useManualCall(memory.callId);
  const call = data ?? null;
  const shown =
    call !== null && (inProgress(call.stage) || (call.stage === "ended" && !wasConnected(call)));
  return { call, shown };
}

/**
 * The ringing call on this phone that is the manual dial coming back, or null. Pure reading;
 * `useOwnCallAutoAnswer` is what acts on it. Split so a view can ask without a second answer
 * being attempted from wherever it is mounted.
 */
export function useOwnManualRinging(): string | null {
  const { user } = useAuth();
  const phone = useSoftphone();
  const memory = useManualDialMemory();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const { data: call } = useManualCall(memory.callId);
  const acd = acdCall(me, phone.calls);
  const number = call && inProgress(call.stage) ? call.phone : null;
  return ownCallToAnswer(
    memory.callId, me?.state?.callerChannelId, phone.calls, acd?.callId ?? null, number,
  );
}

/** While the dial is on its way and a call rings that the rep's record does not name yet: how
 *  often to read the record again, and how many times. */
const CATCH_UP_EVERY_MS = 300;
const CATCH_UP_TIMES = 12;

/**
 * The phone answers the ACD's ring for the call the rep asked for, without a click and whatever
 * auto-answer says: they placed this call, and a lead who answered is waiting on a silent line
 * until it is taken. Not when this browser's auto-answer, or the campaign's, is answering it
 * already — a second answer of the same call fails.
 *
 * The switch's invite usually reaches the browser before the event-tracker's record says whose
 * call it is, so while a call rings and the record does not name it yet, the record is read
 * again every third of a second. Mounted once, in the dock, which is on every screen.
 */
export function useOwnCallAutoAnswer(): void {
  const { user } = useAuth();
  const phone = useSoftphone();
  const toast = useToast();
  const memory = useManualDialMemory();
  const { data: me, refetch } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const { data: call } = useManualCall(memory.callId);
  const acd = acdCall(me, phone.calls);
  const ringing =
    phone.calls.find((c) => c.direction === "inbound" && c.state === "ringing")?.id ?? null;
  const othersAnswer = phone.autoAnswer || forcedAnswer(me, phone.calls, phone.autoAnswer) !== null;
  const waiting = call !== undefined && call !== null && inProgress(call.stage);
  // While the dial is on its way, the call ringing from the number the rep dialed is theirs —
  // usually a moment before the record says so, which is the moment that matters.
  const expected = waiting ? call.phone : null;
  const target = othersAnswer
    ? null
    : ownCallToAnswer(
        memory.callId, me?.state?.callerChannelId, phone.calls, acd?.callId ?? null, expected,
      );
  const answered = React.useRef(new Set<string>());

  // No incoming-call bell for that ring: the card is sounding the ringback, and to the rep this
  // is their own call ringing out, not a call arriving.
  const { expectCall } = phone;
  React.useEffect(() => {
    expectCall(expected);
    return () => expectCall(null);
  }, [expected, expectCall]);

  React.useEffect(() => {
    if (!waiting || ringing === null || target !== null) return;
    let times = 0;
    void refetch();
    const timer = window.setInterval(() => {
      times += 1;
      if (times >= CATCH_UP_TIMES) window.clearInterval(timer);
      void refetch();
    }, CATCH_UP_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [waiting, ringing, target, refetch]);

  const { answer } = phone;
  React.useEffect(() => {
    if (target === null || answered.current.has(target)) return;
    answered.current.add(target);
    answer(target).catch((e: unknown) => {
      // A refused microphone is the usual cause: the call is left ringing for the rep to take.
      console.error("[softphone] could not answer the call the rep placed", e);
      toast.error("Could not answer your call", "Answer it yourself from the phone.");
    });
  }, [target, answer, toast]);
}

/**
 * The console's own ringback while the far end rings. The rep's leg is not up, so the carrier's
 * ringback never reaches them; this is the sound in its place, and it stops the moment the stage
 * moves on — answered, ended, cancelled. Its own `Tones`, beside the phone's: the phone's is
 * driven by its verto calls, and this call is not one of them yet.
 */
function useRingback(on: boolean): void {
  const tones = React.useRef<Tones | null>(null);
  React.useEffect(() => {
    if (!on) {
      tones.current?.play(null);
      return;
    }
    const player = (tones.current ??= new Tones());
    player.setSink(readAudioPrefs().speakerId);
    player.play("ringback");
    return () => player.play(null);
  }, [on]);
  React.useEffect(() => () => tones.current?.dispose(), []);
}

// --- the card ---------------------------------------------------------------------------------------

/**
 * The call the rep placed, as the dock and the Phone page show it: on its way, or how it ended.
 *
 * `wide` is the Phone page's stage, where it is the row a call the dialer places is
 * (`Calling` in `softphone/dialer-offer.tsx`): who and how long with the one control at the end
 * of the bar, the steps on one strip (`DialStrip`, with the dialer's own steps for a dial nothing
 * listens to: called, ringing, answered, your phone), and one line of words under it. To the rep
 * the two are the same thing, an outbound call on its way to them, and it was a card of its own
 * in the middle of the stage, with its own steps under other names and a button across it. The
 * dock, 320px wide, keeps the card.
 */
export function ManualCallView({ wide = false }: { wide?: boolean }) {
  const { call } = useManualCallWork();
  if (!call) return null;
  return call.stage === "ended" ? <Result call={call} wide={wide} /> : <Progress call={call} wide={wide} />;
}

/** Who a call the rep placed is to, as the Phone page's bar says it: the name over the number
 *  and the campaign it goes through, or the number over the campaign for a lead with no name. */
function WideParty({
  call, tone, icon: Icon, children,
}: {
  call: ManualCall;
  /** The mark's ground: the accent on its way, the tone of how it ended once it has. */
  tone: string;
  icon: React.ComponentType<{ className?: string }>;
  /** The end of the bar: the timer and the controls. */
  children: React.ReactNode;
}) {
  const sees = seesCampaigns(useAuth().user?.role);
  const name = leadName(call.lead);
  const number = formatPhoneNumber(call.phone);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-full", tone)}>
        <Icon className="size-[18px]" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 basis-40">
        <p className="truncate text-base font-semibold text-text">{name ?? number}</p>
        <p className="truncate text-xs text-text-muted">
          {name && <span className="font-mono">{number}</span>}
          {sees && name && " · "}
          {sees && (call.campaign_name ?? "Your campaign")}
        </p>
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

function Progress({ call, wide }: { call: ManualCall; wide: boolean }) {
  const sees = seesCampaigns(useAuth().user?.role);
  const phone = useSoftphone();
  const cancel = useCancelManualCall();
  const [error, setError] = React.useState<string | null>(null);
  const now = useNow(1_000);
  const seconds = secondsSince(call.started_at, now);
  // Ringback until the rep's own leg is up: the stage says "connected" only once the row has
  // caught up, and a second of ringback over a live conversation is a second too many.
  const onCall = phone.calls.some((c) => c.state === "active");
  useRingback(playsRingback(call.stage) && !onCall);
  const name = leadName(call.lead);
  const number = formatPhoneNumber(call.phone);
  const steps = progressSteps(call.stage);
  const answered = call.stage === "answered";
  // The ACD ringing this phone with the call: answered without a click a moment later
  // (`useOwnCallAutoAnswer`), and offered here too, for the browser that cannot — a refused
  // microphone leaves the call ringing, and a card with no Answer would leave it ringing for good.
  const ringing = useOwnManualRinging();

  const giveUp = () => {
    setError(null);
    cancel.mutate(call.call_id, {
      onError: (e) =>
        setError(e instanceof ApiError ? e.message : "Could not cancel the call. Try again."),
    });
  };

  if (wide) {
    // A call the rep placed is brought to them the way a dial nothing listens to is, so it has
    // that dial's steps and its words. Once connected the page shows the call itself.
    const stage = call.stage === "connected" || call.stage === "ended" ? "answered" : call.stage;
    const strip: StripPart[] = [{ kind: "now", label: null, steps: dialSteps(stage, null) }];
    const wait = dialWaitText(stage, null);
    return (
      <>
        <div className="@container/dial flex flex-col gap-2 px-4 pb-3 pt-2.5">
          <WideParty call={call} tone="bg-accent-3 text-accent-11" icon={PhoneOutgoing}>
            {call.stage !== "requested" && seconds > 0 && (
              <span className="mr-1 font-mono text-sm tabular text-text" title="Time since the call was placed">
                {formatDuration(seconds)}
              </span>
            )}
            {ringing && (
              <Button
                size="sm"
                className="bg-success-9 hover:brightness-110"
                onClick={() => void phone.answer(ringing)}
                autoFocus
              >
                <Phone />
                Answer
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              loading={cancel.isPending}
              disabled={cancel.isPending}
              title={answered ? "Hangs up the call before it reaches your phone." : "Stops the call before anybody answers."}
              onClick={giveUp}
              autoFocus={ringing === null}
            >
              {!cancel.isPending && <X />}
              {answered ? "Hang up" : "Cancel"}
            </Button>
          </WideParty>
          <DialStrip parts={strip} />
          {/* The words for it, on one line, as under a call the dialer places: until they answer
              the strip has said how far the call has got, so the sentence for that is read out
              and not drawn; from the answer on it leads the line. */}
          <div
            role="status"
            aria-live="polite"
            // `relative`: the containing block of the sentence that is only read out.
            className="relative flex min-h-4 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs"
          >
            <span className={cn("font-semibold text-text", !answered && "sr-only")}>{stageText(call.stage)}</span>
            {!answered && call.caller_id && (
              <span className="text-text">From {formatPhoneNumber(call.caller_id)}.</span>
            )}
            {wait && <span className="text-text-muted">{wait}</span>}
          </div>
        </div>
        {error && (
          <div className="border-t border-border px-4 py-3">
            <Callout tone="danger">{error}</Callout>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      <span
        className={cn(
          "relative flex size-14 items-center justify-center rounded-full text-white",
          answered ? "bg-success-9" : "bg-accent-9",
        )}
      >
        <PhoneOutgoing className="size-6" aria-hidden />
        <span className="animate-pulse-ring" aria-hidden />
      </span>
      <div className="w-full min-w-0">
        <p className="truncate text-lg font-semibold text-text">{name ?? number}</p>
        {name && <p className="truncate font-mono text-xs text-text-muted">{number}</p>}
        <p className="truncate text-xs text-text-muted">
          {sees
            ? `Through ${call.campaign_name ?? "your campaign"}${call.caller_id ? ` · showing ${formatPhoneNumber(call.caller_id)}` : ""}`
            : call.caller_id && `Showing ${formatPhoneNumber(call.caller_id)}`}
        </p>
      </div>

      <ol className="flex w-full items-start gap-1" aria-label="How far the call has got">
        {steps.map((step, i) => (
          <li
            key={step.label}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
            aria-current={step.state === "current" ? "step" : undefined}
          >
            <span
              className={cn(
                "flex size-6 items-center justify-center rounded-full border text-[11px] font-semibold",
                step.state === "done" && "border-success-9 bg-success-9 text-white",
                step.state === "current" && "border-accent-9 text-accent-11",
                step.state === "todo" && "border-border text-text-muted",
              )}
              aria-hidden
            >
              {step.state === "done" ? <Check className="size-3.5" /> : i + 1}
            </span>
            <span
              className={cn(
                "truncate text-[11px]",
                step.state === "todo" ? "text-text-muted" : "font-medium text-text",
              )}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      <p role="status" aria-live="polite" className="text-sm text-text-muted">
        {stageText(call.stage)}
        {call.stage !== "requested" && seconds > 0 && (
          <span className="ml-1 font-mono tabular">{formatDuration(seconds)}</span>
        )}
      </p>

      <div className={cn("grid w-full gap-2", ringing ? "grid-cols-2" : "grid-cols-1")}>
        <Button
          variant="outline"
          className="h-11 text-sm text-danger-11"
          loading={cancel.isPending}
          onClick={giveUp}
          autoFocus={ringing === null}
        >
          <X />
          {answered ? "Hang up" : "Cancel"}
        </Button>
        {ringing && (
          <Button
            variant="default"
            className="h-11 bg-success-9 text-sm hover:brightness-110"
            onClick={() => void phone.answer(ringing)}
            autoFocus
          >
            <Phone />
            Answer
          </Button>
        )}
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}

const RESULT_ICON: Record<FailureKind, React.ComponentType<{ className?: string }>> = {
  no_answer: PhoneMissed,
  busy: PhoneOff,
  cancelled: X,
  not_in_service: Ban,
  blocked: Ban,
  unanswered_by_rep: PhoneMissed,
  failed: AlertTriangle,
};

const RESULT_TONE: Record<FailureKind, string> = {
  no_answer: "bg-warning-3 text-warning-11",
  busy: "bg-warning-3 text-warning-11",
  cancelled: "bg-surface-sunken text-text-muted",
  not_in_service: "bg-danger-3 text-danger-11",
  blocked: "bg-danger-3 text-danger-11",
  unanswered_by_rep: "bg-warning-3 text-warning-11",
  failed: "bg-danger-3 text-danger-11",
};

function Result({ call, wide }: { call: ManualCall; wide: boolean }) {
  const failure = failureText(call);
  const start = useStartManualCall();
  const remember = useSetManualDialMemory();
  const [error, setError] = React.useState<string | null>(null);
  const name = leadName(call.lead);
  const number = formatPhoneNumber(call.phone);
  const rang =
    failure.kind === "no_answer" && call.started_at && call.ended_at
      ? secondsSince(call.started_at, Date.parse(call.ended_at))
      : 0;
  const Icon = RESULT_ICON[failure.kind];

  const again = () => {
    setError(null);
    start.mutate(
      { phone: call.phone, campaignId: call.campaign_id, leadId: placedFor(call) },
      {
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not place the call. Try again."),
      },
    );
  };
  const done = () => remember({ callId: null });

  if (wide) {
    // How it ended is the capsule an attempt that is over has on the dialer's strip, and the two
    // things to do about it are at the end of the bar, where Cancel was.
    const strip: StripPart[] = [{ kind: "made", label: "Your call", text: failure.title }];
    return (
      <>
        <div className="@container/dial flex flex-col gap-2 px-4 pb-3 pt-2.5">
          <WideParty call={call} tone={RESULT_TONE[failure.kind]} icon={Icon}>
            <Button
              variant="outline"
              size="sm"
              disabled={!failure.retryable || start.isPending}
              loading={start.isPending}
              onClick={again}
              autoFocus={failure.retryable}
            >
              {!start.isPending && <RotateCcw />}
              Call again
            </Button>
            <Button variant="outline" size="sm" onClick={done} autoFocus={!failure.retryable}>
              Done
            </Button>
          </WideParty>
          <DialStrip parts={strip} />
          <div role="status" className="relative flex min-h-4 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            <span className="sr-only">{failure.title}.</span>
            <span className="text-text">
              {failure.detail}
              {rang > 0 && ` It rang for ${rang} s.`}
            </span>
            {failure.cause && (
              <span className="font-mono text-[11px] text-text-muted" title="What the carrier reported">
                {failure.cause}
              </span>
            )}
          </div>
        </div>
        {error && (
          <div className="border-t border-border px-4 py-3">
            <Callout tone="danger">{error}</Callout>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      <span
        className={cn(
          "flex size-14 items-center justify-center rounded-full",
          RESULT_TONE[failure.kind],
        )}
      >
        <Icon className="size-6" aria-hidden />
      </span>
      <div className="w-full min-w-0">
        <p role="status" className="text-lg font-semibold text-text">
          {failure.title}
        </p>
        <p className="truncate text-sm text-text">{name ?? number}</p>
        {name && <p className="truncate font-mono text-xs text-text-muted">{number}</p>}
      </div>
      <p className="text-sm text-text-muted">
        {failure.detail}
        {rang > 0 && ` It rang for ${rang} s.`}
      </p>
      {failure.cause && (
        <p className="font-mono text-[11px] text-text-muted" title="What the carrier reported">
          {failure.cause}
        </p>
      )}
      <div className="grid w-full grid-cols-2 gap-2">
        <Button variant="outline" className="h-11 text-sm" onClick={done} autoFocus={!failure.retryable}>
          Done
        </Button>
        <Button
          variant="default"
          className="h-11 text-sm"
          disabled={!failure.retryable}
          loading={start.isPending}
          onClick={again}
          autoFocus={failure.retryable}
        >
          <RotateCcw />
          Call again
        </Button>
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}
